'use client';

import React from 'react';
import { ArrowRight, CheckCircle2, Star } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';

export default function HeroSection() {
  const { t } = useLanguage();

  const TRUST_ITEMS = [
    t('hero.trust1'),
    t('hero.trust2'),
    t('hero.trust3'),
    t('hero.trust4'),
  ];

  const RECENT_ACTIVITY = [
    { action: t('hero.docUploaded'), company: t('hero.activityCompany1'), time: t('hero.timeAgo2m'), dot: 'bg-emerald-400' },
    { action: t('hero.invoicePaid'), company: t('hero.activityCompany2'), time: t('hero.timeAgo18m'), dot: 'bg-blue-400' },
    { action: t('hero.mailForwarded'), company: t('hero.activityCompany3'), time: t('hero.timeAgo1h'), dot: 'bg-[#C9A96E]' },
  ];

  return (
    <section className="relative pt-36 pb-16 lg:pt-40 lg:pb-28 overflow-hidden bg-[#0d1f33]">
      <div
        className="pointer-events-none absolute inset-0 z-0 bg-cover bg-center bg-no-repeat opacity-[0.16]"
        style={{ backgroundImage: 'url(/images/hero-bg.png)' }}
        aria-hidden
      />
      {/* Animated gradient mesh */}
      <div className="absolute inset-0 z-[1] overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-[#1B3A5C] opacity-60 blur-[120px]" />
        <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] rounded-full bg-[#C9A96E]/20 blur-[100px]" />
        <div className="absolute -bottom-20 right-1/3 w-[300px] h-[300px] rounded-full bg-[#1B3A5C]/40 blur-[80px]" />
        {/* Grid overlay */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)',
            backgroundSize: '60px 60px',
          }}
        />
      </div>
      <div className="relative z-10 max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Left: Content */}
          <div>
            {/* Trust badge */}
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#C9A96E]/15 border border-[#C9A96E]/30 text-[#C9A96E] text-xs font-semibold mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C9A96E] animate-pulse" />
              {t('hero.badge')}
            </div>

            <h1 className="font-display text-4xl sm:text-5xl lg:text-[54px] font-bold text-white leading-[1.1] tracking-tight text-balance mb-6">
              <span className="block">{t('hero.title1')}</span>
              {t('hero.title2') ? (
                <span className="block mt-1 bg-gradient-to-r from-[#C9A96E] to-[#e8c98a] bg-clip-text text-transparent">
                  {t('hero.title2')}
                </span>
              ) : null}
              {t('hero.title3') ? (
                <span className="block text-white mt-1">{t('hero.title3')}</span>
              ) : null}
            </h1>

            <p className="text-lg text-slate-300 leading-relaxed mb-8 max-w-xl">
              {t('hero.desc')}
            </p>

            {/* Trust checklist */}
            <ul className="space-y-2.5 mb-10">
              {TRUST_ITEMS?.map((item, i) => (
                <li key={`hero-trust-${i}`} className="flex items-center gap-2.5 text-sm text-slate-300">
                  <CheckCircle2 size={16} className="text-[#C9A96E] shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="#contact"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-[#C9A96E] text-white text-sm font-semibold rounded-lg hover:bg-[#b8944f] active:scale-95 transition-all duration-150 text-center whitespace-normal max-w-full sm:max-w-md"
              >
                {t('hero.cta1')}
                <ArrowRight size={16} className="shrink-0" />
              </a>
              <a href="#how-it-works" className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white text-sm font-semibold rounded-lg hover:bg-white/8 active:scale-95 transition-all duration-150">
                {t('hero.cta2')}
              </a>
            </div>

            {/* Social proof */}
            <div className="flex items-center gap-4 mt-8 pt-8 border-t border-white/10">
              <div className="flex -space-x-2">
                {[
                  { bg: 'bg-[#1B3A5C]', letter: 'M', border: 'border-[#0d1f33]' },
                  { bg: 'bg-[#C9A96E]', letter: 'A', border: 'border-[#0d1f33]' },
                  { bg: 'bg-slate-500', letter: 'D', border: 'border-[#0d1f33]' },
                  { bg: 'bg-emerald-600', letter: 'K', border: 'border-[#0d1f33]' },
                ]?.map((av, i) => (
                  <div key={`avatar-${i}`} className={`w-8 h-8 rounded-full ${av?.bg} border-2 ${av?.border} flex items-center justify-center text-white text-xs font-bold`}>
                    {av?.letter}
                  </div>
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 mb-0.5">
                  {[1, 2, 3, 4, 5]?.map((s) => (
                    <Star key={`star-${s}`} size={12} className="fill-[#C9A96E] text-[#C9A96E]" />
                  ))}
                </div>
                <p className="text-xs text-slate-400">
                  <span className="font-semibold text-white">4.9/5</span> {t('hero.reviews')}
                </p>
              </div>
            </div>
          </div>

          {/* Right: Dashboard mockup */}
          <div className="relative hidden lg:block">
            {/* Main card */}
            <div className="relative bg-white/[0.06] backdrop-blur-xl rounded-2xl border border-white/10 p-6 overflow-hidden shadow-2xl">
              {/* Top bar */}
              <div className="flex items-center justify-between mb-6">
                <div>
                  <p className="text-xs text-slate-400 mb-1">{t('hero.clientPortal')}</p>
                  <p className="font-display font-bold text-white text-lg">{t('hero.mockCompany')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                    {t('hero.active')}
                  </span>
                </div>
              </div>

              {/* KPI grid */}
              <div className="grid grid-cols-2 gap-3 mb-6">
                {[
                  { labelKey: 'hero.documents', value: '14', subKey: 'hero.kpi.documentsSub', color: 'bg-blue-500/10 border-blue-500/20 text-blue-300' },
                  { labelKey: 'hero.invoices', value: '3', subKey: 'hero.kpi.invoicesSub', color: 'bg-amber-500/10 border-amber-500/20 text-amber-300' },
                  { labelKey: 'hero.plan', value: 'Premium', subKey: 'hero.kpi.planSub', color: 'bg-[#C9A96E]/10 border-[#C9A96E]/20 text-[#C9A96E]' },
                  { labelKey: 'hero.address', valueKey: 'hero.kpi.addressValue', subKey: 'hero.kpi.addressSub', color: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-300' },
                ]?.map((card) => (
                  <div key={`hero-card-${card?.labelKey}`} className={`rounded-xl p-3.5 border ${card?.color}`}>
                    <p className="text-xs font-medium opacity-60 mb-1">{t(card?.labelKey)}</p>
                    <p className="font-display font-bold text-lg leading-none text-white">{'value' in card ? card.value : t(card.valueKey!)}</p>
                    <p className="text-xs mt-1 opacity-60">{t(card.subKey)}</p>
                  </div>
                ))}
              </div>

              {/* Activity feed */}
              <div className="space-y-1">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">{t('hero.liveActivity')}</p>
                {RECENT_ACTIVITY?.map((act, i) => (
                  <div key={`act-${i}`} className="flex items-center gap-3 py-2 px-3 rounded-lg bg-white/4 border border-white/5">
                    <span className={`w-2 h-2 rounded-full shrink-0 ${act?.dot}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-200 truncate">{act?.action}</p>
                      <p className="text-[10px] text-slate-500">{act?.company}</p>
                    </div>
                    <span className="text-[10px] text-slate-500 shrink-0">{act?.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}